import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const NarrowRangeGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">BREAKOUT PATTERN</div>
        <h1 className="article-title">How to Trade Narrow Range (NR4 & NR7) Screener</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 8 min read</span>
          <span className="meta-item">⚡ Volatility Expansion Setup</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
          <h2>What are NR4 and NR7 Patterns?</h2>
          <div className="ai-badge-container">
            <span className="ai-badge">📊 Pattern Detected by Screener</span>
            <span className="market-badge">⭐ Tony Crabel's Strategy</span>
          </div>
          <p className="lead-text">
            NR4 and NR7 (Narrow Range 4 and Narrow Range 7) are powerful breakout patterns based on price compression.
            These patterns identify days when the price range is the narrowest compared to recent days, signaling
            an imminent volatility expansion.
          </p>

          <p>
            <strong>NR7:</strong> The day when the price range (High - Low) was the <strong>narrowest in the last seven days</strong><br/>
            <strong>NR4:</strong> The day when the price range (High - Low) was the <strong>narrowest in the last four days</strong>
          </p>

          <div className="highlight-box">
            <strong>Core Philosophy:</strong> A volatility expansion often follows a volatility contraction. When price
            compresses into a narrow range, it's like a coiled spring ready to release energy. The breakout from this
            compressed state typically leads to a significant directional move.
          </div>

          <p>
            This strategy comes from Tony Crabel's book <em>"Day Trading with Short Term Price Patterns & Opening Range
            Breakout"</em> (1990) and is similar in concept to the Bollinger Band Squeeze.
          </p>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📏</div>
              <p className="placeholder-text">NR7 Pattern Illustration</p>
              <p className="placeholder-desc">Shows 7 days with progressively narrowing ranges leading to breakout</p>
            </div>
          </div>
        </section>

        {/* How to Calculate */}
        <section className="content-section">
          <h2>How to Identify NR4 & NR7 Patterns</h2>

          <h3>Calculating NR7 (Step-by-Step)</h3>
          <div className="method-card">
            <ol>
              <li><strong>Get high and low price data</strong> for the last seven days</li>
              <li><strong>Calculate the range</strong> for every day: Range = High - Low</li>
              <li><strong>Compare today's range</strong> to the previous six days' ranges</li>
              <li><strong>If today's range is the smallest</strong> of all seven days, it's an NR7 day</li>
            </ol>

            <div className="example-box">
              <h4>NR7 Calculation Example:</h4>
              <table style={{ width: '100%', marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>High</th>
                    <th>Low</th>
                    <th>Range (H-L)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Day 1</td>
                    <td>₹510</td>
                    <td>₹490</td>
                    <td>₹20</td>
                  </tr>
                  <tr>
                    <td>Day 2</td>
                    <td>₹515</td>
                    <td>₹495</td>
                    <td>₹20</td>
                  </tr>
                  <tr>
                    <td>Day 3</td>
                    <td>₹512</td>
                    <td>₹498</td>
                    <td>₹14</td>
                  </tr>
                  <tr>
                    <td>Day 4</td>
                    <td>₹510</td>
                    <td>₹500</td>
                    <td>₹10</td>
                  </tr>
                  <tr>
                    <td>Day 5</td>
                    <td>₹508</td>
                    <td>₹496</td>
                    <td>₹12</td>
                  </tr>
                  <tr>
                    <td>Day 6</td>
                    <td>₹506</td>
                    <td>₹498</td>
                    <td>₹8</td>
                  </tr>
                  <tr style={{ backgroundColor: '#dafbe1' }}>
                    <td><strong>Day 7</strong></td>
                    <td><strong>₹504</strong></td>
                    <td><strong>₹499</strong></td>
                    <td><strong>₹5</strong></td>
                  </tr>
                </tbody>
              </table>
              <p className="result" style={{ marginTop: '12px' }}>✅ Day 7 is NR7 (smallest range in 7 days)</p>
            </div>
          </div>

          <h3>Calculating NR4</h3>
          <div className="method-card">
            <p>Same process but comparing only the last <strong>4 days</strong>:</p>
            <ul>
              <li>Get high-low data for 4 days</li>
              <li>Calculate range for each day</li>
              <li>If today's range is smallest of 4 days = NR4</li>
            </ul>

            <div className="tip-box">
              <strong>💡 NR7 vs NR4:</strong> NR7 requires higher compression (narrowest in 7 days) and therefore
              often leads to more substantial breakouts. NR4 occurs more frequently but may have slightly lower
              success rates. NR7 is generally preferred for higher-quality setups.
            </div>
          </div>
        </section>

        {/* Trading Strategy */}
        <section className="content-section">
          <h2>NR4 & NR7 Trading Strategy</h2>

          <div className="trailing-rules">
            <div className="rule-card">
              <div className="rule-number">Setup</div>
              <h4>Bullish NR7/NR4 Setup</h4>
              <p>When to look for long positions:</p>
              <ul>
                <li>Stock forms NR7 or NR4 day (narrowest range)</li>
                <li>Preferably in an existing uptrend</li>
                <li>Price consolidating at or near recent highs</li>
                <li>Look to buy on move above the high of the NR day</li>
              </ul>

              <div className="example-calc">
                <p><strong>NR7 Day High:</strong> ₹504</p>
                <p><strong>NR7 Day Low:</strong> ₹499</p>
                <p><strong>Range:</strong> ₹5 (very tight compression)</p>
                <p><strong>Buy Trigger:</strong> Price breaks above ₹504</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Entry</div>
              <h4>Entry Rules</h4>
              <p>Initiate position when:</p>
              <ul>
                <li><strong>Bullish:</strong> Price moves above the high of NR7/NR4 day</li>
                <li><strong>Bearish:</strong> Price moves below the low of NR7/NR4 day</li>
                <li>Wait for candle to close beyond the NR day range</li>
                <li>Can enter on next day's open if gap confirms direction</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Confirmation</div>
              <h4>Additional Confirmation Signals</h4>
              <p>Increase success rate by combining with:</p>
              <ul>
                <li><strong>Trend alignment:</strong> Trade in direction of larger trend</li>
                <li><strong>Volume:</strong> Higher volume on breakout increases reliability</li>
                <li><strong>Support/Resistance:</strong> NR day near key support (bullish) or resistance (bearish)</li>
                <li><strong>Moving averages:</strong> Price above 20/50 SMA for bullish setups</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Timing</div>
              <h4>Trade Must Work Quickly</h4>
              <p>Critical timing consideration:</p>
              <ul>
                <li>Trade should start working <strong>immediately</strong> (within 1-2 days)</li>
                <li>Failure to continue in breakout direction = early warning sign</li>
                <li>Don't give losing trades "more room" - exit quickly</li>
                <li>This is a short-term setup requiring fast confirmation</li>
              </ul>
            </div>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📊</div>
              <p className="placeholder-text">NR7 Breakout Trading Setup</p>
              <p className="placeholder-desc">Shows NR7 formation, buy trigger above high, and stop below low</p>
            </div>
          </div>
        </section>

        {/* Profit Targets */}
        <section className="content-section">
          <h2>Profit Targets and Exit Strategy</h2>

          <h3>Target Setting Methods</h3>
          <div className="method-card">
            <h4>Method 1: Percentage Targets</h4>
            <p>Set realistic percentage-based targets:</p>
            <ul>
              <li>NR7 breakouts: Target 5-15% gain</li>
              <li>NR4 breakouts: Target 3-10% gain</li>
              <li>Scale out at different levels (partial profits)</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Method 2: Resistance Levels</h4>
            <p>Use nearby resistance as targets:</p>
            <ul>
              <li>Previous swing highs</li>
              <li>Round numbers (₹500, ₹550, etc.)</li>
              <li>Fibonacci extension levels</li>
              <li>Take partial profits at each resistance</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Method 3: Trail with Parabolic SAR</h4>
            <p>Let winners run using trailing stop:</p>
            <ul>
              <li>No fixed target - let SAR determine exit</li>
              <li>Captures extended moves automatically</li>
              <li>Exit when price closes opposite SAR</li>
              <li>Best for trending markets</li>
            </ul>
          </div>

          <div className="highlight-box">
            <strong>Realistic Expectations:</strong> NR7 bars typically lead to more substantial breakouts than NR4
            because they represent higher compression. Expect 5-15% moves on successful NR7 breakouts, and 3-10% on
            NR4 breakouts, depending on market conditions and stock volatility.
          </div>
        </section>

        {/* Advanced Tips */}
        <section className="content-section">
          <h2>Advanced NR Trading Tips</h2>

          <h3>Combining NR with Other Indicators</h3>
          <div className="method-card">
            <p>Increase success rate by adding filters:</p>
            <ul>
              <li><strong>Bollinger Bands:</strong> NR day with BB squeeze = powerful combo</li>
              <li><strong>ADX:</strong> ADX rising after NR confirms trend strength</li>
              <li><strong>RSI:</strong> RSI above 50 for bullish, below 50 for bearish</li>
              <li><strong>Moving Averages:</strong> Price above 20/50 EMA strengthens bullish setups</li>
            </ul>
          </div>

          <h3>Best Market Conditions</h3>
          <div className="method-card">
            <p>NR patterns work best when:</p>
            <ul>
              <li><strong>Strong trends:</strong> Trade NR breakouts in direction of trend</li>
              <li><strong>Bull markets:</strong> Higher success rate for bullish NR breakouts</li>
              <li><strong>Post-consolidation:</strong> After extended sideways action</li>
              <li><strong>Earning seasons:</strong> NR before earnings can signal big moves</li>
            </ul>
          </div>

          <h3>NR4 vs NR7: Which to Trade?</h3>
          <div className="method-card">
            <p><strong>NR7 Advantages:</strong></p>
            <ul>
              <li>Higher compression = stronger breakouts</li>
              <li>Better quality setups</li>
              <li>More substantial price moves</li>
              <li>Lower frequency = easier to focus on best opportunities</li>
            </ul>

            <p style={{ marginTop: '16px' }}><strong>NR4 Advantages:</strong></p>
            <ul>
              <li>Occurs more frequently</li>
              <li>More trading opportunities</li>
              <li>Good for active traders</li>
              <li>Still reliable with proper filters</li>
            </ul>

            <div className="tip-box">
              <strong>💡 Recommendation:</strong> Beginners should start with NR7 for higher-quality setups.
              Experienced traders can incorporate both, using NR4 for more opportunities while maintaining
              strict entry criteria.
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="content-section">
          <h2>Common Mistakes to Avoid</h2>

          <div className="mistake-card">
            <h4>❌ Mistake #1: Trading NR Without Trend Context</h4>
            <p>
              Trading NR breakouts against the major trend reduces success rate significantly.
              <strong> Always prefer trading in the direction of the broader trend.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #2: Holding Losing NR Trades</h4>
            <p>
              NR is a short-term setup. If it doesn't work within 1-2 days, the setup has failed.
              <strong> Exit quickly when there's no follow-through after breakout.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #3: Ignoring Volume</h4>
            <p>
              Breakouts on low volume often fail and reverse.
              <strong> Require at least average or above-average volume on the breakout day.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #4: No Confirmation Filters</h4>
            <p>
              Trading every NR day without additional filters leads to many false breakouts.
              <strong> Combine with trend, support/resistance, or moving averages for better results.</strong>
            </p>
          </div>
        </section>

        {/* Real Example */}
        <section className="content-section">
          <h2>Real Trading Example</h2>

          <div className="method-card">
            <h4>NR7 Bullish Breakout Example:</h4>
            <ul>
              <li><strong>Stock:</strong> INFY (in uptrend above 50 SMA)</li>
              <li><strong>NR7 Day:</strong> High ₹1,455 | Low ₹1,450 (₹5 range - narrowest in 7 days)</li>
              <li><strong>Previous 6 Days Ranges:</strong> ₹12, ₹15, ₹18, ₹10, ₹14, ₹11</li>
              <li><strong>Entry:</strong> Buy at ₹1,456 (above NR7 high with volume)</li>
              <li><strong>Compression:</strong> Range compressed 50%+ from average</li>
              <li><strong>Target:</strong> ₹1,472 (2x risk = ₹16 gain)</li>
              <li><strong>Result:</strong> Hit ₹1,475 in 3 days (1.3% gain, 2.4:1 R:R)</li>
            </ul>
          </div>

          <div className="highlight-box">
            <strong>Why This Trade Worked:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li>✅ Stock was in clear uptrend (above 50 SMA)</li>
              <li>✅ NR7 formed near recent highs (bullish consolidation)</li>
              <li>✅ Breakout occurred with above-average volume</li>
              <li>✅ Tight range provided minimal risk entry</li>
              <li>✅ Trade worked immediately (hit target in 3 days)</li>
            </ul>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="content-section">
          <h2>NR4 & NR7 Quick Reference</h2>

          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Element</th>
                  <th>NR4</th>
                  <th>NR7</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Definition</td>
                  <td>Narrowest range in 4 days</td>
                  <td>Narrowest range in 7 days</td>
                </tr>
                <tr>
                  <td>Frequency</td>
                  <td>More common</td>
                  <td>Less common</td>
                </tr>
                <tr>
                  <td>Compression Level</td>
                  <td>Moderate</td>
                  <td>Higher</td>
                </tr>
                <tr>
                  <td>Breakout Power</td>
                  <td>Good (3-10% moves)</td>
                  <td>Better (5-15% moves)</td>
                </tr>
                <tr>
                  <td>Entry</td>
                  <td>Above/below NR day range</td>
                  <td>Above/below NR day range</td>
                </tr>
                <tr>
                  <td>Best For</td>
                  <td>Active traders</td>
                  <td>Quality-focused traders</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="content-section summary-section">
          <h2>Key Takeaways</h2>
          <ul className="takeaway-list">
            <li>✅ NR7/NR4 identify volatility contraction that precedes expansion</li>
            <li>✅ NR7 = narrowest range in 7 days (higher quality, stronger breakouts)</li>
            <li>✅ NR4 = narrowest range in 4 days (more frequent, good for active trading)</li>
            <li>✅ Enter on breakout above (bullish) or below (bearish) the NR day's range</li>
            <li>✅ Place tight stops just beyond the opposite end of NR range (1-2% risk)</li>
            <li>✅ Trade MUST work immediately (1-2 days) - exit if no follow-through</li>
            <li>✅ Best results when combined with trend direction, volume, and support/resistance</li>
            <li>✅ Use Parabolic SAR or trailing stops to manage profitable trades</li>
          </ul>

          <div className="final-note">
            <strong>Remember:</strong> Narrow Range patterns capitalize on the principle that volatility compression
            leads to volatility expansion. The tighter the compression (NR7), the more powerful the eventual breakout.
            Our Narrow Range Screener automatically identifies NR4 and NR7 setups daily, applying trend filters so you
            get only the highest-quality, ready-to-trade opportunities.
          </div>
        </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/flag-pennant-guide')}>
            <div className="related-icon">🏴</div>
            <h4>Flag & Pennant Patterns</h4>
            <p>Learn continuation patterns and breakout timing</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/gap-up-guide')}>
            <div className="related-icon">📊</div>
            <h4>Gap Up Trading Strategy</h4>
            <p>Master gap trading and momentum plays</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub')}>
            <div className="related-icon">📚</div>
            <h4>View All Guides</h4>
            <p>Explore all swing trading tutorials</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Find Narrow Range Setups?</h3>
        <p>Use our screener to find stocks forming NR4 and NR7 patterns ready to breakout</p>
        <button className="cta-button" onClick={() => navigate('/screener/narrow-range')}>
          Try Narrow Range Screener →
        </button>
      </section>

    </div>
  );
};

export default NarrowRangeGuide;
